import styled, { css } from 'styled-components';
import { getSpace, px2rem, mq } from '../../../../stylesheets/tools';
import { fontFamilies } from '../../../../stylesheets/settings';

const backgroundColors = {
  ressources: {
    default: '#9770C8',
    dark: '#64389A'
  },
  agriculture: {
    default: '#A6DD89',
    dark: '#6AA349'
  },
  work: {
    default: '#E26372',
    dark: '#B04B57'
  },
  energy: {
    default: '#AD7496',
    dark: '#92577A'
  },
  nutrition: {
    default: '#45A88F',
    dark: '#308D76'
  },
  health: {
    default: '#EE9E77',
    dark: '#EA7137'
  },
  mobility: {
    default: '#F3C85E',
    dark: '#D39908'
  },
  city: {
    default: '#117D94',
    dark: '#0D6477'
  },
  democracy: {
    default: '#D1578D',
    dark: '#8E3B60'
  }
};

const StyledEducationTabletProgress = styled.section`
  margin-top: auto;
  overflow: hidden;

  .progress__navigation {
    position: relative;
    z-index: 5;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: ${px2rem('250px')};

    ${mq('l')} {
      margin-bottom: ${getSpace('xl')};
    }

    .progress__navigation-link {
      display: flex;
      align-items: center;
      position: relative;

      .progress__navigation-arrow {
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        color: white;
        flex-shrink: 0;

        ${({ category }) =>
          category &&
          css`
            background-color: ${backgroundColors[category]?.default || 'inherit'};
          `};
      }
      .progress__navigation-button {
        font-family: ${fontFamilies.default};
        text-transform: uppercase;
        font-size: ${px2rem('14px')};
        color: black;

        ${mq('l')} {
          font-size: ${px2rem('28px')};
        }

        &:before {
          content: '';
          position: absolute;
          inset: 0;
        }
      }
      &.link--close {
        gap: ${px2rem('20px')};

        .progress__navigation-arrow {
          height: ${px2rem('60px')};
          width: ${px2rem('60px')};

          svg {
            height: ${px2rem('26px')};
            width: ${px2rem('26px')};
          }
        }
      }
      &.link--back,
      &.link--next {
        gap: ${px2rem('20px')};

        .progress__navigation-arrow {
          height: ${px2rem('64px')};
          width: ${px2rem('64px')};

          svg {
            height: ${px2rem('30px')};
            width: ${px2rem('30px')};
          }
        }
      }

      ${mq('l')} {
        &.link--back,
        &.link--next {
          gap: ${px2rem('40px')};

          .progress__navigation-arrow {
            height: ${px2rem('128px')};
            width: ${px2rem('128px')};

            svg {
              height: ${px2rem('60px')};
              width: ${px2rem('60px')};
            }
          }
        }
      }
    }
  }
  .progress__bar {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 6;
    padding: ${px2rem('75px')} 0 0 0;

    ${mq('xl')} {
      padding: ${px2rem('70px')} ${px2rem('60px')} 0 0;
    }

    @media only screen and (min-width: 2000px) {
      padding: ${px2rem('70px')} ${px2rem('60px')} ${px2rem('40px')} 0;
    }

    .progress__bar-background {
      ${({ category }) =>
        category &&
        css`
          background-color: ${backgroundColors[category]?.default || 'inherit'};
        `};
      position: absolute;
      height: 100%;
      left: 0;
      right: 0;
      content: '';
      overflow: hidden;
      transform: translateY(15px) skewY(-3deg);

      ${mq('s')} {
        transform: translateY(10px) skewY(-3deg);
      }

      ${mq('m')} {
        transform: translateY(5px) skewY(-3deg);
      }

      ${mq('l')} {
        transform: translateY(-5%) skewY(-3deg);
      }

      ${mq('xl')} {
        transform: translateY(-10%) skewY(-3deg);
        clip-path: polygon(94% 0%, 100% 50%, 100% 100%, 0 100%, 0 0);
      }

      @media only screen and (min-width: 2000px) {
        transform: translateY(30px) rotate(0deg);
      }
    }
    .progress__bar-steps {
      display: flex;
      align-items: center;
      justify-content: space-between;
      transform: rotate(-3deg);
      transform-origin: center left;

      @media only screen and (min-width: 2000px) {
        transform: rotate(0deg);
      }

      .progress__bar-line {
        width: 100%;
        height: 10px;
        position: relative;

        &::before {
          transition: all ease-in-out 0.5s;
          position: absolute;
          left: 0;
          content: '';
          height: 100%;
          width: 0%;
          background: white;
        }

        ${({ category }) =>
          category &&
          css`
            background-color: ${backgroundColors[category]?.dark || 'inherit'};
          `};

        &.step {
          &--completed,
          &--current {
            &::before {
              width: 100%;
            }
          }
          &--hidden {
            opacity: 0;
          }
        }
        &:first-child {
          max-width: ${px2rem('60px')};
        }
      }
      .progress__bar-step-container {
        position: relative;
        z-index: 5;

        .progress__bar-step-video {
          position: absolute;
          width: 60px;
          z-index: -1;
          left: 50%;
          right: 50%;
          transform: translate(-50%, -20%);
          top: -40px;
          display: none;

          ${mq('xl')} {
            transform: translate(-50%, -60%);
            width: 120px;
          }
        }
        .progress__bar-step {
          position: relative;
          margin: ${px2rem('15px')} 0;
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 0px solid transparent;
          transition: all ease-in-out 0.25s;

          &:before {
            content: '';
            position: absolute;
            top: -9px;
            right: -9px;
            bottom: -9px;
            left: -9px;
            border-style: solid;
            border-width: 10px;
            border-radius: 50%;

            ${({ category }) =>
              category &&
              css`
                border-color: ${backgroundColors[category]?.default || 'inherit'};
              `};

            ${mq('xl')} {
              top: -14px;
              right: -14px;
              bottom: -14px;
              left: -14px;
              border-width: 15px;
            }
          }

          ${mq('xl')} {
            margin: 0;
          }
          span,
          svg {
            transform: rotate(3deg);
          }

          &.step {
            &--completed {
              background-color: white;

              ${({ category }) =>
                category &&
                css`
                  color: ${backgroundColors[category]?.dark || 'inherit'};
                `};
            }
            &--current {
              &:after {
                content: '';
                position: absolute;
                left: 0;
                right: 0;
                top: 0;
                bottom: 0;
                border-radius: 50%;
                border: 6px solid white;

                ${mq('xl')} {
                  border: 8px solid white;
                }
              }
            }
            &--current,
            &--open {
              color: white;

              ${({ category }) =>
                category &&
                css`
                  background-color: ${backgroundColors[category]?.dark || 'inherit'};
                `};
            }
            &--small {
              height: ${px2rem('48px')};
              width: ${px2rem('48px')};

              span {
                font-size: ${px2rem('24px')};
              }
            }
          }
          &:not(.last-step, .step--small) {
            height: ${px2rem('48px')};
            width: ${px2rem('48px')};

            span {
              font-size: ${px2rem('28px')};
            }

            ${mq('xl')} {
              height: ${px2rem('64px')};
              width: ${px2rem('64px')};
              margin: 0px;

              span {
                font-size: ${px2rem('38px')};
              }
            }
          }
          &.last-step {
            margin: 0;
            height: ${px2rem('64px')};
            width: ${px2rem('64px')};

            svg {
              height: ${px2rem('36px')};
              width: ${px2rem('36px')};
            }

            ${mq('xl')} {
              height: ${px2rem('92px')};
              width: ${px2rem('92px')};

              svg {
                height: ${px2rem('40px')};
                width: ${px2rem('40px')};
              }
            }
          }
        }
      }
    }
  }
`;

export default StyledEducationTabletProgress;
